/** Acciones del operador sobre uno o varios QR (R3.2). El backend las recibe en `action`. */
export type BulkAction = 'regen_art' | 'regen_prompt' | 'release_pool' | 'retire' | 'unlink' | 'delete';

export interface BulkActionSpec {
  /** Verbo con el alcance: «Eliminar 4 QR». */
  title: (n: number) => string;
  /** Texto del botón que ejecuta; repite verbo y alcance (design-spec §2.6). */
  cta: (n: number) => string;
  /** Caja ámbar con las reglas de exclusión (R5.2), cuando la acción las tiene. */
  warning?: string;
  /** Qué queda después, en una línea. */
  note?: string;
  /** Rojo y motivo obligatorio: baja, eliminación, quitar código y liberar al pool (R3.3). */
  destructive: boolean;
  /** Chip del resultado por código cuando salió bien, en singular y en plural. */
  doneLabel: string;
  donePlural: string;
  /** Pide un prompt para toda la selección (R3.2). */
  needsPrompt?: boolean;
}

const qrs = (n: number) => (n === 1 ? '1 QR' : `${n} QR`);

export const BULK_ACTIONS: Record<BulkAction, BulkActionSpec> = {
  regen_art: {
    title: (n) => `Regenerar el QR Art de ${qrs(n)}`,
    cta: (n) => `Regenerar ${qrs(n)}`,
    note: 'Se genera una versión nueva con el mismo prompt y otra semilla. Las versiones anteriores se conservan y la principal no cambia hasta que la elijas.',
    destructive: false,
    doneLabel: 'Regenerado',
    donePlural: 'Regenerados',
  },
  regen_prompt: {
    title: (n) => `Nueva versión con otro prompt para ${qrs(n)}`,
    cta: (n) => `Generar ${qrs(n)}`,
    note: 'El mismo prompt se usa en toda la selección. Las regeneraciones del operador no descuentan la cuota del cliente.',
    destructive: false,
    doneLabel: 'Regenerado',
    donePlural: 'Regenerados',
    needsPrompt: true,
  },
  release_pool: {
    title: (n) => `Liberar ${qrs(n)} al pool`,
    cta: (n) => `Liberar ${qrs(n)}`,
    warning: 'Solo se liberan los QR Art sin uso activo. Los físicos en custodia y los que ya tienen escaneos se omiten.',
    note: 'Un código liberado vuelve a quedar disponible para otro pedido.',
    destructive: true,
    doneLabel: 'Liberado',
    donePlural: 'Liberados',
  },
  retire: {
    title: (n) => `Dar de baja ${qrs(n)}`,
    cta: (n) => `Dar de baja ${qrs(n)}`,
    warning: 'Los QR que ya están dados de baja se omiten.',
    note: 'Dejan de redirigir y quedan en cuarentena: no se pueden reasignar a nadie durante ese periodo. Su historial se conserva.',
    destructive: true,
    doneLabel: 'Dado de baja',
    donePlural: 'Dados de baja',
  },
  unlink: {
    title: (n) => (n === 1 ? 'Quitar el código a su organización' : `Quitar ${n} códigos a sus organizaciones`),
    cta: (n) => (n === 1 ? 'Quitar el código' : `Quitar ${n} códigos`),
    note: 'Se da de baja, deja de redirigir, se desvincula de su dueño y queda en cuarentena. Los Owner de esa organización ven el aviso en su consola con el motivo.',
    destructive: true,
    doneLabel: 'Quitado',
    donePlural: 'Quitados',
  },
  delete: {
    title: (n) => `Eliminar ${qrs(n)}`,
    cta: (n) => `Eliminar ${qrs(n)}`,
    warning: 'Solo se eliminan los QR que nunca se activaron, imprimieron ni escanearon. Los demás se omiten y podrás darlos de baja.',
    note: 'Se borran el slot y sus imágenes. El código queda en cuarentena y su historial se conserva aunque el slot ya no exista.',
    destructive: true,
    doneLabel: 'Eliminado',
    donePlural: 'Eliminados',
  },
};

/** Motivo mínimo de R3.3; el mismo número que valida qr-api. */
export const MIN_REASON = 10;

/** Desde acá se pide escribir la cantidad para confirmar (R3.3). */
export const CONFIRM_COUNT_FROM = 10;
